import * as Sentry from '@sentry/browser'
import { LandmarkIcon } from 'lucide-react'
import { useEffect, useState } from 'react'
import { type PlaidInstitution, usePlaidLink } from 'react-plaid-link'
import { toast } from 'sonner'

import { useFlags } from 'pages/employer_onboarding/FlagProvider'
import { parseError } from 'pages/employer_onboarding/utils'
import { Banner, Button, ButtonGroup } from 'slab'
import {
  usePostEmployersBankingPlaidCreateToken as useTokenMutation,
  usePostEmployersBankingPlaidLogEvent as usePlaidEventMutation,
  usePostEmployersBankingPlaidProcessLink as useProcessLinkMutation,
} from 'utils/web_api'

import { BankingSupportModal } from './BankingSupportModal'

/**
 * Plaid's documentation says the `institution` will be either null or an object
 * with a `name` and `institution_id`, but in practice it seems it's sometimes
 * an empty object. We detect that here and coerce it to null.
 */
function normalizedInstitution (institution: any): PlaidInstitution | null {
  if (!institution) return null
  if (typeof institution === 'object' && Object.keys(institution).length === 0) return null
  return institution
}

export function ConnectBanking ({ onBack, onSuccess }: { onBack: () => void, onSuccess: () => void }) {
  const [error, setError] = useState<{
    message: string;
    errors?: string[];
  } | null>(null)
  const [token, setToken] = useState<string | null>(null)

  const { flags: { captureFrontendPlaidEvents = false } } = useFlags()

  const tokenMutation = useTokenMutation()
  const logEventMutation = usePlaidEventMutation()
  const processLinkMutation = useProcessLinkMutation()

  useEffect(() => {
    tokenMutation.mutate(undefined, {
      onSuccess: (data) => {
        setToken(data.link_token)
      },
      onError: (error) => {
        setError(parseError(error, 'Could not generate banking integration token because of an unexpected error.'))
      }
    })
  }, [])

  const plaidLink = usePlaidLink({
    token,
    onSuccess: (publicToken, metadata) => {
      processLinkMutation.mutate({
        data: {
          public_token: publicToken,
          accounts: metadata.accounts,
          institution: normalizedInstitution(metadata.institution),
          link_session_id: metadata.link_session_id,
        }
      },
      {
        onSuccess: async () => {
          onSuccess?.()
        },
        onError: () => {
          toast.error('Could not connect bank account because of an error.')
        }
      })
    },
    onEvent: (eventName, metadata) => {
      let sanitizedMetadata: { [key:string]: string | null } = {
        link_session_id: metadata.link_session_id,
        plaid_request_id: metadata.request_id,
        institution_id: metadata.institution_id || '',
        timestamp: metadata.timestamp || '',
      }

      switch (eventName) {
        case 'ERROR':
        case 'EXIT':
          sanitizedMetadata = {
            ...sanitizedMetadata,
            error_type: metadata.error_type || '',
            error_code: metadata.error_code || '',
            error_message: metadata.error_message || '',
            exit_status: metadata.exit_status || '',
          }
          break
        case 'TRANSITION_VIEW':
          sanitizedMetadata = {
            ...sanitizedMetadata,
            view_name: metadata.view_name,
          }
          break
      }

      const phEventName =
        eventName === 'TRANSITION_VIEW'
          ? `Plaid transition view ${metadata.view_name}`
          : `Plaid event ${eventName}`

      window.posthog?.capture(phEventName, sanitizedMetadata, { timestamp: new Date(metadata.timestamp) })
      Sentry.addBreadcrumb({
        category: 'Plaid',
        message: eventName,
        data: sanitizedMetadata,
      })

      if (captureFrontendPlaidEvents) {
        logEventMutation.mutate({
          data: {
            // using a custom webhook_type naming convention to distinguish from _real_ webhook events
            webhook_type: 'FRONTEND_EVENT',
            webhook_code: eventName,
            metadata: sanitizedMetadata,
          }
        })
      }
    }
  })

  return (
    <div>
      {error && (
        <div className='mb-8'>
          <Banner title='An unexpected error has prevented us from being able to connect your bank account.' type='critical' onDismiss={() => setError(null)}>
            {error.message}
          </Banner>
        </div>
      )}
      <div className='flex flex-col-reverse sm:flex-row items-center sm:justify-between gap-4'>
        <BankingSupportModal />
        <ButtonGroup size='large'>
          <Button variant='secondary' onClick={onBack}>
            Back
          </Button>
          <Button
            disabled={!plaidLink.ready}
            leftIcon={LandmarkIcon}
            variant='primary'
            onClick={() => plaidLink.open()}
          >
            Connect your bank account
          </Button>
        </ButtonGroup>
      </div>
    </div>
  )
}
